import { PropertyHelper } from '@/helpers/property.helper';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import {
  ActionState,
  Document,
  DocumentExport,
  DocumentExportParams,
  RealProperty,
  VaultService
} from '@eemo/api-client';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgTemplateOutlet, NgFor, NgIf } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';

export class PropertyDocExportDialogData {
  property: RealProperty;
}

export interface DocGroup {
  docs: Document[];
}

@Component({
    selector: 'app-property-doc-export-dialog',
    templateUrl: './property-doc-export-dialog.component.html',
    styleUrls: ['./property-doc-export-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, FormsModule, ReactiveFormsModule, MatCheckboxModule, NgFor, NgIf, MatProgressBarModule, MatButtonModule, TranslateModule]
})
export class PropertyDocExportDialogComponent implements OnInit, OnDestroy {
  dialogTitle = 'propertyDocExportDialog.title';
  isLoading = false;
  getDocumentsSub = new Subscription();
  exportDocumentsSub = new Subscription();
  getExportDocumentsSub = new Subscription();

  documents: Document[] = [];
  allChecked = false;
  forceDocChecked: string[] = [];
  currentExport: DocumentExport = null;
  docsSelectDisabled = false;
  exportDownloadUrl: string;

  timer: NodeJS.Timeout;

  ActionState = ActionState;

  get form_documents() {
    return this.form.get('documents') as FormControl<Document[]>;
  }

  form = this.fb.group({
    documents: [[], Validators.required]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyDocExportDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PropertyDocExportDialogComponent>,
    private vaultService: VaultService,
    private translate: TranslateService,
    private propHelper: PropertyHelper
  ) {}

  ngOnInit(): void {
    this.getPropertyExport();
    this.getRealPropertyDocuments();
    this.exportDownloadUrl = this.propHelper.getExportPropertyDocumentsUrl(this.data.property);
  }

  ngOnDestroy(): void {
    this.getDocumentsSub.unsubscribe();
    this.exportDocumentsSub.unsubscribe();
    this.getExportDocumentsSub.unsubscribe();
    this.stopAutoRefresh();
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.export();
    }
  }

  afterGetDocs(docs: Document[]) {
    this.documents = docs;
  }

  afterGetExport(currentExport: DocumentExport) {
    this.currentExport = currentExport;
    this.forceDocChecked = [];
    this.docsSelectDisabled = currentExport !== null;
    if (currentExport) {
      if (currentExport.state !== ActionState.IN_PROGRESS) {
        this.stopAutoRefresh();
      }
      currentExport.docs.forEach((uid) => (this.forceDocChecked[uid] = true));
    }
  }

  onClickAllDocument(event: MatCheckboxChange) {
    const checked = event.checked;
    if (checked) {
      this.addAll();
    } else {
      this.removeAll();
    }
  }

  onClickDocument(event: MatCheckboxChange, item: Document) {
    const checked = event.checked;
    if (checked) {
      this.addDocument(item);
    } else {
      this.removeDocument(item);
    }
  }

  addAll() {
    this.form_documents.setValue(this.documents);
    this.allChecked = true;
  }

  removeAll() {
    this.form_documents.setValue([]);
    this.allChecked = false;
  }

  addDocument(doc: Document) {
    const docs = [...this.form_documents.value];
    docs.push(doc);
    this.form_documents.setValue(docs);
  }

  removeDocument(doc: Document) {
    const i = this.form_documents.value.findIndex((d: Document) => d.uid === doc.uid);
    if (i >= 0) {
      const docs = [...this.form_documents.value];
      docs.splice(i, 1);
      this.form_documents.setValue(docs);
    }
  }

  checkDocument(docId: string) {
    this.form_documents.value.findIndex((d: Document) => d.uid === docId);
  }

  startAutoRefresh() {
    this.timer = setInterval(() => this.getPropertyExport(), 2000);
  }

  stopAutoRefresh() {
    clearInterval(this.timer);
  }

  cancelExport() {
    this.currentExport = null;
    this.docsSelectDisabled = false;
    this.forceDocChecked = [];
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  getRealPropertyDocuments() {
    this.isLoading = true;
    this.getDocumentsSub = this.vaultService.getRealPropertyDocuments(this.data.property.uid).subscribe({
      next: (docs: Document[]) => {
        this.isLoading = false;
        this.afterGetDocs(docs);
      },
      error: (err: any) => {
        this.isLoading = false;
      }
    });
  }

  getPropertyExport() {
    this.isLoading = true;
    this.getExportDocumentsSub = this.vaultService.getExportPropertyDocuments(this.data.property.uid).subscribe({
      next: (currentExport: DocumentExport) => {
        this.isLoading = false;
        this.afterGetExport(currentExport);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  export() {
    this.isLoading = true;
    const body = this.getExportBody();
    this.exportDocumentsSub = this.vaultService.exportPropertyDocuments(this.data.property.uid, body).subscribe({
      next: () => {
        this.isLoading = false;
        this.currentExport = { state: ActionState.IN_PROGRESS };
        this.startAutoRefresh();
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  getExportBody(): DocumentExportParams {
    const body: DocumentExportParams = {
      docs: this.form_documents?.value.map((d) => d.uid)
    };

    return body;
  }
}
